// Portfolio Administration v1.0 PR 4a §7.4 — Active Policies table.

import { Check, X as XIcon } from "lucide-react";
import { Table, THead, TBody, TR, TH, TD, Badge } from "@/design-system/components/ui";
import {
  bandBorderClass,
  bandForDueDate,
  daysLabel,
  daysUntil,
  calendarDate,
  formatDate,
} from "../common/colorBand";
import { isLapsed } from "./policyStatus";
import type { InsurancePolicyDto } from "@/api/portfolioAdmin/types";

interface ActivePoliciesTableProps {
  policies: InsurancePolicyDto[];
  onRowClick: (policy: InsurancePolicyDto) => void;
}

// `tz` is REQUIRED here, not optional. This badge is the third read of one
// countdown in a row that also prints a day label and paints a border band;
// making the zone omissible would let a future caller produce a row whose
// three cells disagree about what day it is.
function statusBadge(p: InsurancePolicyDto) {
  const days = daysUntil(p.expirationDate);
  if (isLapsed(p)) {
    return <Badge status="error">Lapsed</Badge>;
  }
  if (days !== null && days <= 30) {
    return <Badge status="review">Expires {days}d</Badge>;
  }
  return <Badge status="finalized">Active</Badge>;
}

function fmtMoney(n: number | null): string {
  if (n === null) return "—";
  return `$${n.toLocaleString()}`;
}

function verifiedCell(date: string | null) {
  return date ? (
    <span className="inline-flex items-center gap-space-1 text-status-success">
      <Check size={14} aria-hidden /> {formatDate(date)}
    </span>
  ) : (
    <span className="inline-flex items-center gap-space-1 text-status-error">
      <XIcon size={14} aria-hidden /> Missing
    </span>
  );
}

export function ActivePoliciesTable({
  policies,
  onRowClick,
}: ActivePoliciesTableProps) {
  // Above the early return — a hook after a conditional `return` is a rules-
  // of-hooks violation, and the empty case is the first statement here.
  if (policies.length === 0) {
    return (
      <p className="text-small text-gray-mid italic m-0">
        No insurance policies recorded for this loan.
      </p>
    );
  }
  return (
    <Table data-testid="insurance-active-table">
      <THead>
        <TR>
          <TH className="text-white-pure">Policy type</TH>
          <TH className="text-white-pure">Carrier</TH>
          <TH className="text-white-pure">Policy #</TH>
          <TH className="text-white-pure">Coverage</TH>
          <TH className="text-white-pure">Effective</TH>
          <TH className="text-white-pure">Expiration</TH>
          <TH className="text-white-pure">Phoenix</TH>
          <TH className="text-white-pure">Seller</TH>
          <TH className="text-white-pure">Days</TH>
          <TH className="text-white-pure">Status</TH>
        </TR>
      </THead>
      <TBody>
        {policies.map((p) => {
          const days = daysUntil(p.expirationDate);
          const band = bandForDueDate(p.expirationDate);
          return (
            <TR
              key={p.id}
              onClick={() => onRowClick(p)}
              className={`cursor-pointer border-l-4 ${bandBorderClass(band)}`}
              data-testid={`insurance-row-${p.id}`}
            >
              <TD>{p.policyType}</TD>
              <TD>{p.carrierName ?? "—"}</TD>
              <TD className="font-mono text-caption">{p.policyNumber ?? "—"}</TD>
              <TD>{fmtMoney(p.coverageAmount)}</TD>
              <TD>{calendarDate(p.effectiveDate)}</TD>
              <TD>{calendarDate(p.expirationDate)}</TD>
              <TD>{verifiedCell(p.phoenixNamedAsVerifiedDate)}</TD>
              <TD>{verifiedCell(p.sellerNamedAsVerifiedDate)}</TD>
              <TD>{daysLabel(days)}</TD>
              <TD>{statusBadge(p)}</TD>
            </TR>
          );
        })}
      </TBody>
    </Table>
  );
}
